import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

type UseScrollToTopOnRouteChangeOptions = {
  containerId?: string;
  behavior?: ScrollBehavior;
  scrollOnPop?: boolean;
};

/**
 * Scrolls the app container on route transitions while preserving each entry's position.
 */
const useScrollToTopOnRouteChange = (options: UseScrollToTopOnRouteChangeOptions = {}) => {
  const { containerId = "root", behavior = "auto", scrollOnPop = false } = options;
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const positionsMap = positions.current;
    if (navigationType === "POP") {
      if (scrollOnPop) scrollToTop(containerId, "auto");
      else {
        const savedPosition = positionsMap.get(location.key);
        if (savedPosition !== undefined) scrollToTop(containerId, "auto", savedPosition);
      }
    } else {
      scrollToTop(containerId, behavior);
    }

    return () => {
      const container = document.getElementById(containerId);
      positionsMap.set(location.key, container?.scrollTop ?? window.scrollY);
    };
  }, [behavior, containerId, location.key, navigationType, scrollOnPop]);
};

const scrollToTop = (containerId: string, behavior: ScrollBehavior, top = 0) => {
  const container = document.getElementById(containerId);
  if (container) {
    container.scrollTo({ top, behavior });
    return;
  }

  window.scrollTo({ top, behavior });
};

export { useScrollToTopOnRouteChange };
